import { DESIGN_HEIGHT, DESIGN_WIDTH, frontalQuad, project, toPoints } from '../geometry';
import type { Vec3 } from '../geometry';
import { boxFaces, seeded, worldPoints, scope } from './kit';
import { FLOOR, floorCamera as cam, floorRegions } from './floor.geometry';
import { PAL } from './palette';
import { overlayQuad } from './regions';
import type { ArtProps } from './types';

const { half, zNear, zWall, height } = FLOOR;
const P = (x: number, y: number, z: number) => project(cam, [x, y, z]);

/** A circle lying flat at height `y`, as world points. */
function ring(cx: number, y: number, cz: number, radius: number, sides = 36): Vec3[] {
  return Array.from({ length: sides }, (_, index) => {
    const angle = (index / sides) * Math.PI * 2;
    return [cx + Math.cos(angle) * radius, y, cz + Math.sin(angle) * radius] as const;
  });
}

// the window in the left wall (x = -half)
const WINDOW = { z0: 1.5, z1: 4.9, y0: 0.32, y1: 2.6, transom: 2.05 } as const;
const PANES = [
  [1.5, 2.55],
  [2.62, 3.67],
  [3.74, 4.82],
] as const;
const SUN = { x: 1, y: -0.8, z: 0.25 } as const;

/** Where a point of the window lands on the floor along the sun's direction. */
function onFloor(z: number, y: number): [number, number, number] {
  const travel = y / -SUN.y;
  return [-half + SUN.x * travel, 0, z + SUN.z * travel];
}

// ── Back: the room around the floor ─────────────────────────────────────────────────────────────

export function FloorBack({ ids }: ArtProps) {
  const { id, ref } = scope(ids, 'back');

  const backWall = frontalQuad(cam, -half, half, 0, height, zWall);
  const leftWall: Vec3[] = [
    [-half, height, zNear - 2],
    [-half, height, zWall],
    [-half, 0, zWall],
    [-half, 0, zNear - 2],
  ];
  const rightWall: Vec3[] = [
    [half, height, zNear - 2],
    [half, height, zWall],
    [half, 0, zWall],
    [half, 0, zNear - 2],
  ];
  const ceiling: Vec3[] = [
    [-half, height, zNear - 2],
    [half, height, zNear - 2],
    [half, height, zWall],
    [-half, height, zWall],
  ];

  // window on the left wall, door opening on the right
  const win = (z0: number, z1: number, y0: number, y1: number): Vec3[] => [
    [-half, y1, z0],
    [-half, y1, z1],
    [-half, y0, z1],
    [-half, y0, z0],
  ];
  const door: Vec3[] = [
    [half, 2.25, 2.3],
    [half, 2.25, 3.7],
    [half, 0, 3.7],
    [half, 0, 2.3],
  ];
  const doorInner: Vec3[] = [
    [half, 2.15, 2.4],
    [half, 2.15, 3.6],
    [half, 0, 3.6],
    [half, 0, 2.4],
  ];

  const skirt = (a: Vec3, b: Vec3): Vec3[] => [
    [a[0], 0.14, a[2]],
    [b[0], 0.14, b[2]],
    [b[0], 0, b[2]],
    [a[0], 0, a[2]],
  ];

  const artCentre = P(0, 1.78, zWall);
  const artRadius = 0.5 * (820 / (zWall + 2.6));

  return (
    <g>
      <defs>
        <linearGradient id={id('back')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#4a443e" />
          <stop offset="1" stopColor="#5d564d" />
        </linearGradient>
        <linearGradient
          id={id('left')}
          x1="0"
          y1="0"
          x2="1"
          y2="0"
          gradientUnits="objectBoundingBox"
        >
          <stop offset="0" stopColor="#2c2926" />
          <stop offset="1" stopColor="#5a534a" />
        </linearGradient>
        <linearGradient id={id('right')} x1="1" y1="0" x2="0" y2="0">
          <stop offset="0" stopColor="#282522" />
          <stop offset="1" stopColor="#4b453f" />
        </linearGradient>
        <linearGradient id={id('ceil')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1d1b19" />
          <stop offset="1" stopColor="#34302c" />
        </linearGradient>
        <linearGradient id={id('day')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f4f8fa" />
          <stop offset="1" stopColor="#cfe0ea" />
        </linearGradient>
        <linearGradient id={id('hall')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#0c0b0b" />
          <stop offset="1" stopColor="#4a3b2a" />
        </linearGradient>
        <linearGradient id={id('brass')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={PAL.brassHi} />
          <stop offset="0.55" stopColor={PAL.brass} />
          <stop offset="1" stopColor={PAL.brassLo} />
        </linearGradient>
        <radialGradient id={id('wash')} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor={PAL.warm} stopOpacity="0.5" />
          <stop offset="1" stopColor={PAL.warm} stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width={DESIGN_WIDTH} height={DESIGN_HEIGHT} fill="#1a1918" />
      <polygon points={worldPoints(cam, ceiling)} fill={ref('ceil')} />
      <polygon points={toPoints(backWall)} fill={ref('back')} />
      <polygon points={worldPoints(cam, leftWall)} fill={ref('left')} />
      <polygon points={worldPoints(cam, rightWall)} fill={ref('right')} />

      {/* a slim cove light along the ceiling */}
      <polyline
        points={worldPoints(cam, [
          [-half + 0.02, height - 0.08, zNear],
          [-half + 0.02, height - 0.08, zWall],
          [half - 0.02, height - 0.08, zWall],
          [half - 0.02, height - 0.08, zNear],
        ])}
        fill="none"
        stroke={PAL.warm}
        strokeWidth="2"
        strokeOpacity="0.55"
      />

      {/* back wall: a brass disc over the sofa, a thin console */}
      <circle cx={artCentre[0]} cy={artCentre[1]} r={artRadius} fill={ref('brass')} />
      <circle
        cx={artCentre[0]}
        cy={artCentre[1]}
        r={artRadius * 0.82}
        fill="#2a2725"
        opacity="0.55"
      />
      <ellipse
        cx={artCentre[0]}
        cy={artCentre[1] + artRadius * 1.5}
        rx={artRadius * 3.2}
        ry={artRadius * 0.9}
        fill={ref('wash')}
      />

      {/* the window: black steel, four daylight panes and a transom */}
      <polygon
        points={worldPoints(
          cam,
          win(WINDOW.z0 - 0.07, WINDOW.z1 + 0.07, WINDOW.y0 - 0.07, WINDOW.y1 + 0.07),
        )}
        fill="#101113"
      />
      {PANES.map(([z0, z1]) => (
        <polygon
          key={z0}
          points={worldPoints(cam, win(z0, z1, WINDOW.y0, WINDOW.y1))}
          fill={ref('day')}
        />
      ))}
      <polyline
        points={worldPoints(cam, [
          [-half, WINDOW.transom, WINDOW.z0 - 0.05],
          [-half, WINDOW.transom, WINDOW.z1 + 0.05],
        ])}
        stroke="#101113"
        strokeWidth="6"
        fill="none"
      />

      {/* the doorway on the right: dark, with a warm glow at the end of the hall */}
      <polygon points={worldPoints(cam, door)} fill={PAL.brassLo} />
      <polygon points={worldPoints(cam, doorInner)} fill={ref('hall')} />

      {/* skirting */}
      <polygon
        points={worldPoints(cam, skirt([-half, 0, zNear - 2], [-half, 0, zWall]))}
        fill="#0f0e0d"
      />
      <polygon
        points={worldPoints(cam, skirt([half, 0, zNear - 2], [half, 0, zWall]))}
        fill="#0f0e0d"
      />
      <polygon
        points={worldPoints(cam, skirt([-half, 0, zWall], [half, 0, zWall]))}
        fill="#0c0b0b"
      />
    </g>
  );
}

// ── Shade ───────────────────────────────────────────────────────────────────────────────────────

export function FloorShade({ ids }: ArtProps) {
  const { id } = scope(ids, 'shade');
  const [floor] = floorRegions;
  const wallTopLeft = P(-half, 0, zWall);
  const wallTopRight = P(half, 0, zWall);
  const backV = wallTopLeft[1];
  return (
    <g>
      <defs>
        <linearGradient
          id={id('depth')}
          x1="0"
          y1={backV}
          x2="0"
          y2="800"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#000" stopOpacity="0.32" />
          <stop offset="0.25" stopColor="#000" stopOpacity="0.06" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={id('left')}
          x1={wallTopLeft[0]}
          y1="0"
          x2={wallTopLeft[0] + 120}
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#000" stopOpacity="0.3" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={id('right')}
          x1={wallTopRight[0]}
          y1="0"
          x2={wallTopRight[0] - 120}
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#000" stopOpacity="0.3" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </linearGradient>
        <radialGradient
          id={id('vignette')}
          cx="600"
          cy="560"
          r="760"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(600 560) scale(1 0.6) translate(-600 -560)"
        >
          <stop offset="0.5" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.4" />
        </radialGradient>
      </defs>
      {floor ? (
        <>
          <polygon points={toPoints(overlayQuad(floor))} fill={`url(#${id('depth')})`} />
          <polygon points={toPoints(overlayQuad(floor))} fill={`url(#${id('left')})`} />
          <polygon points={toPoints(overlayQuad(floor))} fill={`url(#${id('right')})`} />
          <polygon points={toPoints(overlayQuad(floor))} fill={`url(#${id('vignette')})`} />
        </>
      ) : null}
    </g>
  );
}

// ── Sheen ───────────────────────────────────────────────────────────────────────────────────────

export function FloorSheen({ ids }: ArtProps) {
  const { id } = scope(ids, 'sheen');
  const far = P(0, 0, zWall)[1];
  return (
    <g>
      <defs>
        <filter id={id('soft')} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="7" />
        </filter>
        <filter id={id('softer')} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="22" />
        </filter>
        <linearGradient
          id={id('gloss')}
          x1="0"
          y1={far}
          x2="0"
          y2="800"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#fff" stopOpacity="0.6" />
          <stop offset="0.35" stopColor="#fff" stopOpacity="0.16" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* the sky behind the polished floor: a glossy falloff from the far wall */}
      <rect
        x="0"
        y={far}
        width={DESIGN_WIDTH}
        height={DESIGN_HEIGHT - far}
        fill={`url(#${id('gloss')})`}
      />
      {/* the window's light, one soft parallelogram per pane */}
      <g filter={`url(#${id('soft')})`} fill="#fff" opacity="0.85">
        {PANES.map(([z0, z1]) => (
          <polygon
            key={z0}
            points={worldPoints(cam, [
              onFloor(z0, WINDOW.y0),
              onFloor(z1, WINDOW.y0),
              onFloor(z1, WINDOW.transom),
              onFloor(z0, WINDOW.transom),
            ])}
          />
        ))}
      </g>
      {/* a long pool of light from the door end */}
      <ellipse
        cx="860"
        cy={far + 40}
        rx="140"
        ry="22"
        fill={PAL.warm}
        opacity="0.5"
        filter={`url(#${id('softer')})`}
      />
    </g>
  );
}

// ── Front: furniture and everything standing on the stone ───────────────────────────────────────

export function FloorFront({ ids }: ArtProps) {
  const { id, ref } = scope(ids, 'front');
  const rng = seeded(21);

  const seatBox = { x0: -1.5, x1: 1.5, y0: 0.06, y1: 0.4, z0: 5.1, z1: 6.05 };
  const backBox = { x0: -1.5, x1: 1.5, y0: 0.4, y1: 0.86, z0: 5.82, z1: 6.05 };
  const armL = { x0: -1.5, x1: -1.22, y0: 0.4, y1: 0.62, z0: 5.1, z1: 6.05 };
  const armR = { x0: 1.22, x1: 1.5, y0: 0.4, y1: 0.62, z0: 5.1, z1: 6.05 };
  const sofa = [seatBox, backBox, armL, armR].map((box) => boxFaces(cam, box));
  const cushions = [-1.0, 0, 1.0].map((cx) =>
    boxFaces(cam, { x0: cx - 0.46, x1: cx + 0.46, y0: 0.4, y1: 0.5, z0: 5.2, z1: 5.8 }),
  );

  const tableTop = ring(0, 0.4, 3.9, 0.62).map((p) => P(p[0], p[1], p[2]));
  const tableEdge = ring(0, 0.35, 3.9, 0.62).map((p) => P(p[0], p[1], p[2]));
  const tableShadow = ring(0.05, 0, 3.95, 0.72).map((p) => P(p[0], p[1], p[2]));
  const sofaShadow = [P(-1.6, 0, 5.05), P(1.6, 0, 5.05), P(1.75, 0, 6.15), P(-1.75, 0, 6.15)];

  // floor lamp
  const lampBase = P(1.95, 0, 5.45);
  const lampTop = P(1.95, 1.72, 5.45);
  const lampHead = P(1.45, 1.78, 5.45);
  const lampScale = 820 / (5.45 + 2.6);

  // plant
  const potBase = P(-2.05, 0, 5.6);
  const potTop = P(-2.05, 0.42, 5.6);
  const plantScale = 820 / (5.6 + 2.6);
  const leaves = Array.from({ length: 12 }, (_, index) => {
    const angle = -90 + (index - 5.5) * 13 + (rng() - 0.5) * 12;
    const rad = (angle * Math.PI) / 180;
    const length = (0.5 + rng() * 0.45) * plantScale;
    const baseX = potTop[0];
    const baseY = potTop[1];
    const tipX = baseX + Math.cos(rad) * length;
    const tipY = baseY + Math.sin(rad) * length;
    const width = (0.09 + rng() * 0.06) * plantScale;
    const midX = (baseX + tipX) / 2;
    const midY = (baseY + tipY) / 2;
    return {
      d: `M${baseX.toFixed(1)} ${baseY.toFixed(1)}Q${(midX - Math.sin(rad) * width).toFixed(1)} ${(midY + Math.cos(rad) * width).toFixed(1)} ${tipX.toFixed(1)} ${tipY.toFixed(1)}Q${(midX + Math.sin(rad) * width).toFixed(1)} ${(midY - Math.cos(rad) * width).toFixed(1)} ${baseX.toFixed(1)} ${baseY.toFixed(1)}Z`,
      fill: index % 3 === 0 ? PAL.leafHi : index % 3 === 1 ? PAL.leaf : PAL.leafLo,
    };
  });

  const faceFill = (face: 'front' | 'top' | 'left' | 'right') =>
    face === 'front' ? '#6f675c' : face === 'top' ? '#9a9184' : '#554e45';

  return (
    <g>
      <defs>
        <filter id={id('shadow')} x="-20%" y="-60%" width="140%" height="220%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
        <linearGradient id={id('cushion')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a39a8d" />
          <stop offset="1" stopColor="#7f776b" />
        </linearGradient>
        <linearGradient id={id('wood')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3b2f25" />
          <stop offset="1" stopColor="#1d1611" />
        </linearGradient>
        <linearGradient id={id('pot')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={PAL.pot} />
          <stop offset="1" stopColor={PAL.potLo} />
        </linearGradient>
        <radialGradient id={id('lamp')} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor={PAL.warm} stopOpacity="0.8" />
          <stop offset="1" stopColor={PAL.warm} stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id={id('vignette')}
          cx="600"
          cy="420"
          r="780"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(600 420) scale(1 0.72) translate(-600 -420)"
        >
          <stop offset="0.55" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.55" />
        </radialGradient>
      </defs>

      {/* soft contact shadows on the stone */}
      <g filter={`url(#${id('shadow')})`} fill="#000">
        <polygon points={toPoints(sofaShadow)} opacity="0.55" />
        <polygon points={toPoints(tableShadow)} opacity="0.5" />
        <ellipse
          cx={lampBase[0]}
          cy={lampBase[1]}
          rx={lampScale * 0.3}
          ry={lampScale * 0.07}
          opacity="0.5"
        />
        <ellipse
          cx={potBase[0]}
          cy={potBase[1]}
          rx={plantScale * 0.4}
          ry={plantScale * 0.1}
          opacity="0.55"
        />
      </g>

      {/* sofa */}
      {sofa.map((faces, index) => (
        <g key={index}>
          {faces.left ? <polygon points={toPoints(faces.left)} fill={faceFill('left')} /> : null}
          {faces.right ? <polygon points={toPoints(faces.right)} fill={faceFill('right')} /> : null}
          <polygon points={toPoints(faces.front)} fill={faceFill('front')} />
          {faces.top ? <polygon points={toPoints(faces.top)} fill={faceFill('top')} /> : null}
        </g>
      ))}
      {cushions.map((faces, index) => (
        <g key={index}>
          <polygon points={toPoints(faces.front)} fill={ref('cushion')} />
          {faces.top ? <polygon points={toPoints(faces.top)} fill="#aaa192" /> : null}
        </g>
      ))}

      {/* coffee table */}
      <polygon points={toPoints(tableEdge)} fill="#120d0a" />
      <polygon points={toPoints(tableTop)} fill={ref('wood')} />
      <polyline
        points={toPoints(tableTop)}
        fill="none"
        stroke={PAL.brass}
        strokeOpacity="0.55"
        strokeWidth="1.2"
      />

      {/* floor lamp */}
      <ellipse
        cx={lampBase[0]}
        cy={lampBase[1]}
        rx={lampScale * 0.16}
        ry={lampScale * 0.04}
        fill="#1a1a1c"
      />
      <path
        d={`M${lampBase[0]} ${lampBase[1]}L${lampTop[0]} ${lampTop[1]}Q${lampTop[0] - 40} ${lampTop[1] - 70} ${lampHead[0]} ${lampHead[1]}`}
        fill="none"
        stroke={PAL.brassLo}
        strokeWidth="4"
        strokeLinecap="round"
      />
      <ellipse cx={lampHead[0]} cy={lampHead[1] + 18} rx="52" ry="38" fill={ref('lamp')} />
      <path
        d={`M${lampHead[0] - 18} ${lampHead[1] + 16}Q${lampHead[0]} ${lampHead[1] - 14} ${lampHead[0] + 18} ${lampHead[1] + 16}Z`}
        fill={PAL.brass}
      />

      {/* plant */}
      {leaves.map((leaf, index) => (
        <path key={index} d={leaf.d} fill={leaf.fill} />
      ))}
      <path
        d={`M${potTop[0] - plantScale * 0.2} ${potTop[1]}L${potTop[0] + plantScale * 0.2} ${potTop[1]}L${potBase[0] + plantScale * 0.14} ${potBase[1]}L${potBase[0] - plantScale * 0.14} ${potBase[1]}Z`}
        fill={ref('pot')}
      />

      <rect width={DESIGN_WIDTH} height={DESIGN_HEIGHT} fill={ref('vignette')} />
    </g>
  );
}
